import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Plus, Trash2, Loader2, Mail,
  FileText, User, Pen, Copy, CheckCircle2, GripVertical, X
} from "lucide-react";

import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { LOGO_URL } from "../config/constants";

// Subfolder options for SharePoint destination
const SUBFOLDER_OPTIONS = [
  "Compliance and Financials",
  "Attendances",
  "Documents for Signature",
  "Original signed documents received",
  "Survey",
  "Mortgage offer",
  "Draft Buildings Insurance",
  "Searches and Indemnity",
  "Post completion and evidence for the Land Registry",
  "Redemption",
];

// Preset packs for common transaction types
const PRESET_PACKS = [
  {
    key: "residential_purchase_fh",
    label: "FH Purchase",
    description: "Standard freehold purchase pack",
    templates: ["photo_id", "proof_of_address", "mortgage_offer", "gifted_deposit"],
  },
  {
    key: "residential_sale",
    label: "Residential Sale",
    description: "Standard pack for selling a property",
    templates: ["photo_id", "proof_of_address", "title_deeds", "lease", "insurance"],
  },
  {
    key: "remortgage",
    label: "Remortgage",
    description: "Documents for a remortgage transaction",
    templates: ["photo_id", "proof_of_address", "mortgage_offer", "title_deeds", "insurance"],
  },
  {
    key: "leasehold_purchase",
    label: "LH Purchase",
    description: "Leasehold purchase pack (includes lease docs)",
    templates: ["photo_id", "proof_of_address", "mortgage_offer", "gifted_deposit", "lease", "insurance"],
  },
  {
    key: "company_purchase",
    label: "Company / Corporate Purchase",
    description: "Corporate buyer with company due diligence docs",
    templates: ["photo_id", "proof_of_address", "company_docs", "accounts", "mortgage_offer"],
  },
];

// Common document templates for quick-add
const DOC_TEMPLATES = [
  { key: "photo_id", label: "Photo ID", description: "Passport or driving licence" },
  { key: "proof_of_address", label: "Proof of Address", description: "Utility bill or bank statement (within 3 months)" },
  { key: "mortgage_offer", label: "Mortgage Offer", description: "Copy of your mortgage offer (if applicable)" },
  { key: "gifted_deposit", label: "Gifted Deposit Letter", description: "Signed letter from donor if deposit is a gift" },
  { key: "company_docs", label: "Company Documents", description: "Certificate of incorporation, articles of association" },
  { key: "accounts", label: "Latest Accounts", description: "Most recent filed accounts or management accounts" },
  { key: "title_deeds", label: "Title Deeds", description: "Copy of title deeds or Land Registry entries" },
  { key: "lease", label: "Lease Agreement", description: "Copy of the lease (if leasehold)" },
  { key: "planning_permission", label: "Planning Permission", description: "Planning consent documents (if applicable)" },
  { key: "insurance", label: "Insurance Documents", description: "Buildings insurance or indemnity policy" },
];

// ── Cover-email bullet generator ───────────────────────────────────────────
//
// Friendly client-facing labels for the bulleted list in the cover email.
// Keys MUST stay in sync with the backend `DOC_FRIENDLY_LABELS` map in
// `backend/hub/client_portal.py`.  When a checklist item is missing from this
// map (e.g. a custom item the fee-earner added on the fly), we fall back to
// the item's own `label` field, then to a title-cased version of its key.
const DOC_FRIENDLY_LABELS = {
  photo_id:            "Photo ID (passport or driving licence)",
  proof_of_address:    "Proof of address (utility bill or bank statement, within 3 months)",
  mortgage_offer:      "Mortgage offer",
  gifted_deposit:      "Gifted Deposit letter (signed by the donor)",
  company_docs:        "Company documents (certificate of incorporation, articles)",
  accounts:            "Latest filed accounts or management accounts",
  title_deeds:         "Title deeds or Land Registry entries",
  lease:               "Lease agreement",
  planning_permission: "Planning permission documents",
  insurance:           "Buildings insurance or indemnity policy",
};

const getFriendlyLabel = (item) =>
  DOC_FRIENDLY_LABELS[item.key]
  || item.label
  || (item.key || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Hidden markers that bracket the auto-generated bullet block so we can
// detect manual edits AND regenerate cleanly on Reset.  Plain-text comments
// — the email body editor is plain-text in v1.
const BULLETS_START = "[--auto-bullets--]";
const BULLETS_END   = "[--/auto-bullets--]";

/**
 * Build the bulleted list (between hidden markers) from the current checklist.
 * Each checklist item becomes one friendly-labelled bullet.
 */
export function buildBulletBlock(checklist, baseUrl = "", portalToken = null) {
  if (!checklist || checklist.length === 0) {
    return `${BULLETS_START}\n${BULLETS_END}`;
  }
  const lines = checklist.map(item => {
    const label = getFriendlyLabel(item);
    return `\u2022 ${label}`;
  });
  return `${BULLETS_START}\n${lines.join("\n")}\n${BULLETS_END}`;
}

/**
 * Compose the full default cover-email body, with auto-generated bullets
 * dropped into the middle.  Plain text in v1 — matches the existing
 * `_send_portal_email` template style.
 */
export function composeDefaultEmailBody({
  salutation, clientName, matterReference, feeEarnerName,
  checklist, baseUrl, portalToken,
}) {
  const greeting = (salutation || clientName || "Sir/Madam").trim();
  const lawyer   = feeEarnerName || "Your Bonsai Law solicitor";
  const matter   = matterReference || "your matter";
  const bullets  = buildBulletBlock(checklist, baseUrl, portalToken);
  return (
`Dear ${greeting},

In order to progress ${matter}, we need a few documents from you so we can carry out our standard onboarding checks. Most clients find this takes around 15–30 minutes, and you can save your progress and come back as many times as you need.

Here is the list of items we'd be grateful for:

${bullets}

You can upload everything securely via your private portal — the link will appear in the "Upload Documents" button below.

If you could let us have these within the next 7 days that would be a great help in keeping things moving.

If you have any questions about any of the above, please reply to this email and we'll be happy to talk you through it.

Yours sincerely,
${lawyer}`
  );
}

/**
 * Pure helper that replaces the bullet block (between the hidden markers)
 * with a freshly-generated one.  Returns the new body OR the original body
 * if the markers can't be found (which indicates the user has manually
 * edited the block — we should NOT clobber their wording).
 */
export function replaceBulletBlock(body, newBlock) {
  const startIdx = body.indexOf(BULLETS_START);
  const endIdx   = body.indexOf(BULLETS_END);
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return body;
  return body.slice(0, startIdx) + newBlock + body.slice(endIdx + BULLETS_END.length);
}

/**
 * Detect whether the auto-bullet block in `body` matches what we'd
 * generate from the current `checklist`.  Used after every keystroke to
 * decide whether to keep auto-update on.
 */
export function bulletBlockIsCanonical(body, checklist, baseUrl, portalToken) {
  const canonical = buildBulletBlock(checklist, baseUrl, portalToken);
  const startIdx = body.indexOf(BULLETS_START);
  const endIdx   = body.indexOf(BULLETS_END);
  if (startIdx === -1 || endIdx === -1) return false;
  return body.slice(startIdx, endIdx + BULLETS_END.length) === canonical;
}


export default function ClientDocRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);

  // Form fields
  const [matterReference, setMatterReference] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [jointClientName, setJointClientName] = useState("");
  const [jointClientEmail, setJointClientEmail] = useState("");
  const [salutation, setSalutation] = useState("");
  const [subfolderName, setSubfolderName] = useState("Compliance and Financials");
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  // v1 default body — kept for backward compatibility & for the "outside the
  // bullet block" surrounding text.  The bullet block itself is regenerated
  // from the checklist below via buildBulletBlock / composeDefaultEmailBody.
  const defaultEmailBody = "In order to proceed with your matter, we require some documents from you.\n\nPlease use the secure link below to upload the requested items.";
  const [emailBody, setEmailBody] = useState("");
  // Auto-update tracker: stays TRUE until the user manually edits the bullet
  // block (or breaks the hidden markers).  When TRUE, ticking a doc adds
  // a bullet within 100ms; unticking removes it.  Set to FALSE on any
  // manual edit that breaks the canonical bullet block.
  const [emailBodyAutoUpdate, setEmailBodyAutoUpdate] = useState(true);
  const [emailBodyTouched, setEmailBodyTouched] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Return-to path (set when navigated from a specific matter)
  const [returnTo, setReturnTo] = useState(null);

  // Shopping list
  const [checklist, setChecklist] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemDesc, setNewItemDesc] = useState("");

  // Submission state
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Matter search for auto-fill
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [searchingMatters, setSearchingMatters] = useState(false);
  const [linkedSubmissionId, setLinkedSubmissionId] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  // Pre-fill from URL params when navigated from PostIntakeDetail or similar
  useEffect(() => {
    const ref = searchParams.get("matter_ref");
    const client = searchParams.get("client");
    const email = searchParams.get("email");
    const ret = searchParams.get("return_to");

    if (ref) setMatterReference(ref);
    if (client) {
      setClientName(client);
      setSalutation(client.split(" ")[0]);
    }
    if (email) setClientEmail(email);
    if (ret) setReturnTo(ret);
  }, [searchParams]);

  // Debounced matter search
  useEffect(() => {
    if (matterQuery.length < 2) { setMatterResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchingMatters(true);
      try {
        const res = await axios.get(`${API}/submissions`, { params: { search: matterQuery, limit: 5 } });
        setMatterResults(res.data?.submissions || []);
      } catch { setMatterResults([]); }
      setSearchingMatters(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [matterQuery]);

  // ── Email body auto-update ────────────────────────────────────────────
  //
  // Whenever the checklist changes (tick / untick), regenerate the bullet
  // block in-place (between the hidden markers) within 100ms — unless the
  // user has manually edited the block, in which case we leave their copy
  // intact.  See `bulletBlockIsCanonical` for the canonical-shape check.
  useEffect(() => {
    const t = setTimeout(() => {
      setEmailBody(prev => {
        // First load (no body yet) — render the full default template.
        if (!prev || !emailBodyTouched) {
          const fresh = composeDefaultEmailBody({
            salutation, clientName, matterReference,
            feeEarnerName: user?.name || user?.email || "",
            checklist, baseUrl: window.location.origin, portalToken: null,
          });
          return fresh;
        }
        // Subsequent change — only patch the bullet block (preserves any
        // surrounding edits the user has made).  If markers are gone,
        // leave the body alone entirely.
        if (!emailBodyAutoUpdate) return prev;
        const newBlock = buildBulletBlock(checklist, window.location.origin, null);
        const patched = replaceBulletBlock(prev, newBlock);
        return patched;
      });
    }, 90);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist, salutation, clientName, matterReference, user?.name, user?.email]);

  const handleEmailBodyChange = (e) => {
    const v = e.target.value;
    setEmailBody(v);
    setEmailBodyTouched(true);
    // If the bullet block no longer matches what we'd auto-generate, switch
    // auto-update off so we don't clobber the user's wording on the next
    // tick.  Note: this means deleting/editing ANYTHING between the markers
    // disables live updates — that's the design.
    const isCanonical = bulletBlockIsCanonical(
      v, checklist, window.location.origin, null,
    );
    setEmailBodyAutoUpdate(isCanonical);
  };

  const resetEmailBodyToDefault = () => {
    const fresh = composeDefaultEmailBody({
      salutation, clientName, matterReference,
      feeEarnerName: user?.name || user?.email || "",
      checklist, baseUrl: window.location.origin, portalToken: null,
    });
    setEmailBody(fresh);
    setEmailBodyAutoUpdate(true);
    setEmailBodyTouched(false);
  };

  const selectMatter = (sub) => {
    const d = sub.data || {};
    setMatterReference(d.Q4 || d.Q3 || sub.matter_reference || "");
    setClientName(d.Q1 || "");
    setClientEmail(d.client_email || d.Q2 || "");
    setSalutation((d.Q1 || "").split(" ")[0]);
    setLinkedSubmissionId(sub.id);
    setMatterQuery("");
    setMatterResults([]);
    toast.success(`Linked to ${d.Q4 || sub.matter_reference || "matter"}`);
  };

  const addTemplateItem = (tpl) => {
    if (checklist.some(c => c.key === tpl.key)) return;
    setChecklist(prev => [...prev, { ...tpl, required: true }]);
  };

  const applyPreset = (pack) => {
    const newItems = [];
    pack.templates.forEach(key => {
      if (!checklist.some(c => c.key === key)) {
        const tpl = DOC_TEMPLATES.find(t => t.key === key);
        if (tpl) newItems.push({ ...tpl, required: true });
      }
    });
    if (newItems.length === 0) {
      toast.info("All documents from this pack are already in your list");
      return;
    }
    setChecklist(prev => [...prev, ...newItems]);
    toast.success(`Added ${newItems.length} document${newItems.length !== 1 ? "s" : ""} from ${pack.label}`);
  };

  const addCustomItem = () => {
    if (!newItemLabel.trim()) return;
    const key = `custom_${Date.now()}`;
    setChecklist(prev => [...prev, { key, label: newItemLabel.trim(), description: newItemDesc.trim(), required: true }]);
    setNewItemLabel("");
    setNewItemDesc("");
  };

  const removeItem = (key) => {
    setChecklist(prev => prev.filter(c => c.key !== key));
  };

  const toggleRequired = (key) => {
    setChecklist(prev => prev.map(c => c.key === key ? { ...c, required: !c.required } : c));
  };

  const handleSend = async () => {
    if (!clientName.trim() || !clientEmail.trim()) {
      toast.error("Client name and email are required");
      return;
    }
    if (!matterReference.trim()) {
      toast.error("Please enter a matter reference");
      return;
    }

    setSending(true);
    try {
      const payload = {
        submission_id: linkedSubmissionId || null,
        matter_reference: matterReference.trim(),
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        fee_earner_name: user?.name || "",
        fee_earner_email: user?.email || "",
        is_residential: true,
        joint_client_name: jointClientName.trim() || null,
        joint_client_email: jointClientEmail.trim() || null,
        salutation: salutation.trim() || null,
        // Strip the hidden auto-bullet markers before sending — the client
        // shouldn't ever see them.  Send the body if it's non-empty;
        // backend renders it inline in place of the boilerplate.
        email_body: emailBody.trim()
          ? emailBody.replace(/\[--auto-bullets--\]\n?/g, "").replace(/\n?\[--\/auto-bullets--\]/g, "").trim()
          : null,
        subfolder_name: subfolderName,
        checklist: checklist.length > 0 ? checklist : null,
      };

      const res = await axios.post(`${API}/portal/create`, payload);
      if (res.data.success) {
        setResult(res.data);
        toast.success("Document request sent to client!");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to create portal"));
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setChecklist([]);
    setClientName("");
    setClientEmail("");
    setMatterReference("");
    setLinkedSubmissionId(null);
    setSalutation("");
    setSubfolderName("Compliance and Financials");
    setReturnTo(null);
  };

  const portalUrl = result ? `${process.env.REACT_APP_BACKEND_URL || ""}/portal/${result.token}` : "";

  return (
    <div className="min-h-screen bg-navy">
      {/* Header */}
      <div className="bg-navy border-b border-navy-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => returnTo ? navigate(returnTo) : navigate("/")}
              className="text-txt-muted hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src={LOGO_URL} alt="Bonsai" className="h-8 w-8 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-white">Client Document Request</h1>
              <p className="text-xs text-txt-muted">Request documents from a client via secure portal</p>
            </div>
          </div>
          {returnTo && (
            <button
              onClick={() => navigate(returnTo)}
              className="text-xs text-txt-muted hover:text-white flex items-center gap-1"
              data-testid="back-to-matter-header-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to matter
            </button>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {result ? (
          /* Success state */
          <div className="rounded-md border border-navy-border bg-navy p-8 text-center" data-testid="portal-created-success">
            <CheckCircle2 className="w-12 h-12 text-teal mx-auto mb-4" />
            <h2 className="text-xl font-bold text-txt mb-2">Document Request Sent</h2>
            <p className="text-txt-muted mb-6">
              An email has been sent to <span className="text-txt font-medium">{clientEmail}</span> with a link to upload their documents.
            </p>
            <div className="bg-navy-card rounded-md p-4 mb-6 flex items-center justify-between gap-3">
              <code className="text-sm text-cyan-300 truncate flex-1">{portalUrl}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copied!"); }}
                className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                data-testid="copy-portal-url"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <p className="text-xs text-txt-muted mb-6">
              {result.checklist_count} document{result.checklist_count !== 1 ? "s" : ""} requested · Expires {new Date(result.expires_at).toLocaleDateString("en-GB")}
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-navy-border text-txt hover:bg-navy-hover"
                data-testid="new-request-btn"
              >
                New Request
              </Button>
              {returnTo ? (
                <Button
                  onClick={() => navigate(returnTo)}
                  className="bg-teal hover:bg-teal-hover text-white"
                  data-testid="back-to-matter-btn"
                >
                  Back to Matter
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/")}
                  className="bg-teal hover:bg-teal-hover text-white"
                  data-testid="back-to-dashboard-btn"
                >
                  Back to Dashboard
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Form */
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left — Client details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Link to existing matter (optional) */}
              <div className="rounded-md border border-navy-border bg-navy p-5" data-testid="matter-link-section">
                <h3 className="text-sm font-semibold text-txt mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-teal" /> Link to Matter (optional)
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    value={matterQuery}
                    onChange={e => setMatterQuery(e.target.value)}
                    placeholder="Search by matter ref, client name..."
                    className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-teal/50"
                    data-testid="matter-search-input"
                  />
                  {searchingMatters && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-txt-muted animate-spin" />}
                  {matterResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-navy-card border border-navy-border rounded-md shadow-xl max-h-48 overflow-y-auto">
                      {matterResults.map((sub, i) => (
                        <button
                          key={sub.id || i}
                          onClick={() => selectMatter(sub)}
                          className="w-full text-left px-3 py-2 hover:bg-navy-hover border-b border-navy-border last:border-0"
                          data-testid={`matter-result-${i}`}
                        >
                          <span className="text-sm text-txt font-medium">{sub.data?.Q4 || sub.matter_reference || sub.id}</span>
                          <span className="text-xs text-txt-muted block">{sub.data?.Q1 || ""} · {sub.data?.Q3 || sub.secondary_label || ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {linkedSubmissionId && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-teal">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Linked to {matterReference}
                    <button onClick={() => { setLinkedSubmissionId(null); setMatterReference(""); }} className="text-txt-muted hover:text-status-red ml-auto"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </div>

              {/* Client details */}
              <div className="rounded-md border border-navy-border bg-navy p-5 space-y-4" data-testid="client-details-section">
                <h3 className="text-sm font-semibold text-txt flex items-center gap-2">
                  <User className="w-4 h-4 text-cyan-400" /> Client Details
                </h3>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Matter Reference *</label>
                  {/* Brief #4 — SmartClientMatterSearch rollout (Block 3 table
                      row #13: feeEarnerScoped=true; the component does not
                      expose a scoping prop, intent captured here as code
                      intent). */}
                  <SmartClientMatterSearch
                    label={null}
                    placeholder="BL.VEC.001.0100"
                    initialValue={matterReference}
                    onSelect={(result) => {
                      const ref = result.matter_reference || result.label || "";
                      setMatterReference(ref);
                      if (!clientName && result.client_name) {
                        setClientName(result.client_name);
                        if (!salutation) setSalutation(result.client_name.split(" ")[0]);
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Client Name *</label>
                  <input type="text" value={clientName} onChange={e => { setClientName(e.target.value); if (!salutation) setSalutation(e.target.value.split(" ")[0]); }} className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50" placeholder="Alice Johnson" data-testid="client-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Client Email *</label>
                  <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50" placeholder="alice@example.com" data-testid="client-email-input" />
                </div>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Salutation</label>
                  <input type="text" value={salutation} onChange={e => setSalutation(e.target.value)} className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50" placeholder="Dear Alice" data-testid="salutation-input" />
                </div>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Joint Client Name (optional)</label>
                  <input type="text" value={jointClientName} onChange={e => setJointClientName(e.target.value)} className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50" placeholder="" data-testid="joint-name-input" />
                </div>
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Joint Client Email (optional)</label>
                  <input type="email" value={jointClientEmail} onChange={e => setJointClientEmail(e.target.value)} className="w-full px-3 py-2 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50" placeholder="" data-testid="joint-email-input" />
                </div>

                {/* Email customisation — auto-populated bullets, manual edit safe */}
                <div data-testid="email-customisation-block">
                  <div className="flex items-center justify-between">
                    <button onClick={() => setShowEmailEditor(!showEmailEditor)} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="toggle-email-editor">
                      <Pen className="w-3 h-3" /> {showEmailEditor ? "Hide" : "Customise"} email
                    </button>
                    {showEmailEditor && (
                      <div className="flex items-center gap-2">
                        {!emailBodyAutoUpdate && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            title="The bullet block has been manually edited — adding/removing items in the checklist will no longer auto-update the email body. Click 'Reset to default' to re-sync."
                            data-testid="email-autoupdate-off-indicator"
                          >
                            Auto-update off
                          </span>
                        )}
                        <button
                          onClick={resetEmailBodyToDefault}
                          className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
                          data-testid="email-reset-default-btn"
                        >
                          Reset to default
                        </button>
                        <button
                          onClick={() => setShowEmailPreview(!showEmailPreview)}
                          className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
                          data-testid="email-toggle-preview-btn"
                        >
                          {showEmailPreview ? "Hide preview" : "Preview"}
                        </button>
                      </div>
                    )}
                  </div>
                  {showEmailEditor && (
                    <>
                      <textarea
                        value={emailBody}
                        onChange={handleEmailBodyChange}
                        className="w-full mt-2 px-3 py-2 text-xs bg-navy-card border border-navy-border rounded-md text-txt resize-y focus:ring-1 focus:ring-cyan-500/50 font-mono"
                        rows={16}
                        data-testid="email-body-textarea"
                        spellCheck="true"
                      />
                      <p className="text-[10px] text-txt-muted mt-1 italic">
                        Tip: the bulleted list between the <code>[--auto-bullets--]</code> markers updates live when you tick documents on the right. Edit anywhere outside those markers freely; edit inside them to switch to manual mode.
                      </p>
                      {showEmailPreview && (
                        <div
                          className="mt-3 p-3 bg-navy-card border border-navy-border rounded-md text-xs text-txt-muted whitespace-pre-wrap font-mono max-h-80 overflow-y-auto"
                          data-testid="email-preview-pane"
                        >
                          {emailBody
                            .replace(/\[--auto-bullets--\]\n?/g, "")
                            .replace(/\n?\[--\/auto-bullets--\]/g, "")
                            .trim()}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Destination subfolder */}
                <div>
                  <label className="block text-xs text-txt-muted mb-1">Save documents to</label>
                  <Select value={subfolderName} onValueChange={setSubfolderName}>
                    <SelectTrigger id="subfolder" className="w-full bg-navy-card border-navy-border text-txt text-sm focus:ring-1 focus:ring-cyan-500/50" data-testid="subfolder-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBFOLDER_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-txt-muted mt-1">Documents will be saved to this folder within the matter's SharePoint file.</p>
                </div>
              </div>
            </div>

            {/* Right — Shopping List */}
            <div className="lg:col-span-3 space-y-6">
              <div className="rounded-md border border-navy-border bg-navy overflow-hidden" data-testid="shopping-list-section">
                <div className="bg-navy-card px-5 py-3 border-b border-navy-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-semibold text-txt">Document Checklist</span>
                    <span className="text-xs text-txt-muted">({checklist.length} item{checklist.length !== 1 ? "s" : ""})</span>
                  </div>
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    data-testid="toggle-templates-btn"
                  >
                    <Plus className="w-3 h-3" /> Quick Add
                  </button>
                </div>

                {/* Preset Packs */}
                <div className="px-5 py-3 border-b border-navy-border bg-navy-card/50">
                  <p className="text-xs font-semibold text-txt-muted mb-2">Preset Packs — one-click to add a standard set:</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="preset-packs-section">
                    {PRESET_PACKS.map(pack => (
                      <button
                        key={pack.key}
                        onClick={() => applyPreset(pack)}
                        className="text-xs px-3 py-1.5 rounded-md border border-teal/40 bg-teal/10 text-teal hover:bg-teal/20 hover:border-teal/50 transition-colors"
                        data-testid={`preset-${pack.key}`}
                        title={pack.description}
                      >
                        {pack.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Template picker */}
                {showTemplates && (
                  <div className="px-5 py-3 border-b border-navy-border bg-navy-card">
                    <p className="text-xs text-txt-muted mb-2">Click to add common document types:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {DOC_TEMPLATES.map(tpl => {
                        const added = checklist.some(c => c.key === tpl.key);
                        return (
                          <button
                            key={tpl.key}
                            onClick={() => !added && addTemplateItem(tpl)}
                            disabled={added}
                            className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${added ? "bg-teal/20 border-teal/40 text-teal cursor-default" : "border-navy-border text-txt-muted hover:border-cyan-500/30 hover:text-cyan-300"}`}
                            data-testid={`template-${tpl.key}`}
                          >
                            {added && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                            {tpl.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Current checklist */}
                <div className="px-5 py-4">
                  {checklist.length === 0 ? (
                    <div className="text-center py-8" data-testid="empty-checklist">
                      <FileText className="w-8 h-8 text-txt-faint mx-auto mb-2" />
                      <p className="text-sm text-txt-muted mb-1">No documents requested yet</p>
                      <p className="text-xs text-txt-faint">Use "Quick Add" above or add a custom item below.<br/>Leave empty to use the default checklist.</p>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="checklist-items">
                      {checklist.map((item, i) => (
                        <div key={item.key} className="flex items-start gap-3 bg-navy-card rounded-md p-3 border border-navy-border group" data-testid={`checklist-item-${i}`}>
                          <GripVertical className="w-4 h-4 text-txt-faint mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-txt">{item.label}</p>
                            {item.description && <p className="text-xs text-txt-muted mt-0.5">{item.description}</p>}
                          </div>
                          <button
                            onClick={() => toggleRequired(item.key)}
                            className={`text-[10px] px-2 py-0.5 rounded border flex-shrink-0 ${item.required ? "bg-teal/10 border-teal/30 text-teal" : "bg-transparent border-navy-border text-txt-muted"}`}
                            data-testid={`toggle-required-${i}`}
                          >
                            {item.required ? "Required" : "Optional"}
                          </button>
                          <button
                            onClick={() => removeItem(item.key)}
                            className="text-txt-faint hover:text-status-red flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-item-${i}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Custom item input */}
                  <div className="mt-4 pt-4 border-t border-navy-border" data-testid="custom-item-section">
                    <p className="text-xs text-txt-muted mb-2">Add custom item:</p>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newItemLabel}
                        onChange={e => setNewItemLabel(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addCustomItem()}
                        placeholder="Document label"
                        className="flex-1 px-3 py-1.5 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50"
                        data-testid="custom-label-input"
                      />
                      <button
                        onClick={addCustomItem}
                        disabled={!newItemLabel.trim()}
                        className="px-3 py-1.5 text-sm bg-teal/20 border border-teal/40 text-teal rounded-md hover:bg-teal/30 disabled:opacity-40"
                        data-testid="add-custom-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={newItemDesc}
                      onChange={e => setNewItemDesc(e.target.value)}
                      placeholder="Description (optional)"
                      className="w-full px-3 py-1.5 text-sm bg-navy-card border border-navy-border rounded-md text-txt focus:ring-1 focus:ring-cyan-500/50"
                      data-testid="custom-desc-input"
                    />
                  </div>
                </div>
              </div>

              {/* Send button */}
              <Button
                onClick={handleSend}
                disabled={sending || !clientName.trim() || !clientEmail.trim() || !matterReference.trim()}
                className="w-full bg-teal hover:bg-teal-hover text-white gap-2"
                data-testid="send-request-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sending ? "Sending..." : "Send Document Request"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
